import { isNil } from "es-toolkit";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { ConvexProvider, ConvexReactClient } from "convex/react";
import { LazyMotion, MotionConfig, domAnimation } from "motion/react";
import { RegistryProvider } from "@effect-atom/atom-react";
import { registerSW } from "virtual:pwa-register";
import { routeTree } from "@/routeTree.gen";
import { env } from "@/shared/config/env";
import { initSentry } from "@/shared/config/sentry";
import { AnatomyModel } from "@/entities/muscle/ui/anatomy-model.ui";
import { pwaUpdateModel } from "@/features/pwa-update/pwa-update.model";
// oxlint-disable-next-line import/no-unassigned-import -- global stylesheet side effect
import "./styles/globals.css";

// @react-three/fiber 9.6 still does `new THREE.Clock()`; three r183+ warns on
// every construction. Suppress only that exact message until fiber updates.
const originalWarn = console.warn;
console.warn = (...args: Array<unknown>): void => {
  if (
    typeof args[0] === "string" &&
    args[0].includes("THREE.Clock: This module has been deprecated")
  )
    return;

  originalWarn(...args);
};

const purgeStaleServiceWorker = async (): Promise<void> => {
  const registrations = await navigator.serviceWorker.getRegistrations();
  if (registrations.length === 0) return;

  await Promise.all(registrations.map((registration) => registration.unregister()));
  if ("caches" in self) {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  }

  location.reload();
};

if (env.DEV && "serviceWorker" in navigator) {
  void purgeStaleServiceWorker();
}

window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason as { name?: string } | null | undefined;
  if (reason?.name === "InvalidStateError") event.preventDefault();
});

const updateSW = env.PROD
  ? registerSW({
      immediate: true,
      onNeedRefresh(): void {
        pwaUpdateModel.notifyAvailable(updateSW);
      },
    })
  : (): Promise<void> => Promise.resolve();
AnatomyModel.preload("/zanatomy_muscular.glb");

const convex = new ConvexReactClient(env.VITE_CONVEX_URL);
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPreloadDelay: 50,
  defaultViewTransition: {
    // eslint-disable-next-line sonarjs/function-return-type -- TanStack Router's view transition `types` callback returns `Array<string> | false`
    types: ({ pathChanged, hashChanged }): Array<string> | false => {
      if (!pathChanged && !hashChanged) return false;

      return pathChanged ? ["route-change"] : ["hash-change"];
    },
  },
});

declare module "@tanstack/react-router" {
  // oxlint-disable-next-line typescript/consistent-type-definitions -- declaration merging requires interface
  interface Register {
    router: typeof router;
  }
}

initSentry(router);

const rootEl = document.getElementById("root");
if (isNil(rootEl)) throw new Error("Root element #root not found");

createRoot(rootEl).render(
  <ConvexProvider client={convex}>
    <RegistryProvider>
      <LazyMotion features={domAnimation} strict>
        <MotionConfig reducedMotion={env.VITE_E2E ? "always" : "user"}>
          <RouterProvider router={router} />
        </MotionConfig>
      </LazyMotion>
    </RegistryProvider>
  </ConvexProvider>,
);
